import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { ICustomerDTO } from 'data/dtos/operation/i-customer-dto'
import { ChangeEvent, MouseEvent, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from 'services/api'
import { loadList } from 'services/requests'
import useContract from 'store/contracts'
import filterFormatter from 'utils/filter-forrmat'
import generateLabelNames from 'utils/label-names'
import extractState from 'utils/organization/extract-state'

const useComponent = () => {
  const [page, setPage] = useState<number>(0)
  const [filter, setFilter] = useState<{
    search: string
    status: string
  }>({
    search: '',
    status: 'Todos',
  })

  const [rowsPerPage, setRowsPerPage] = useState<number>(100)
  const [columnOrder, setColumnOrder] = useState<('ASC' | 'DESC')[]>([])
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [recordToDelete, setRecordToDelete] = useState<string | null>('')

  const [inviteToEdit, setInviteToEdit] = useState<ICustomerDTO | null>(null)
  const [inviteEmail, setInviteEmail] = useState<string>('')
  const [isSavingInviteEmail, setIsSavingInviteEmail] = useState<boolean>(false)

  const { user } = useAuth()
  const history = useHistory()
  const { openAlert } = useAlert()
  const { updateOrganization } = useContract()

  const label = user ? generateLabelNames(user.isSeller!, user.isAdmin!) : ({} as any)

  const list = useQuery({
    queryKey: ['customers', 'list', filter.search, filter.status, page, rowsPerPage, columnOrder],
    queryFn: () =>
      loadList('/organizations/list', {
        status: filterFormatter(filter.status),
        search: filterFormatter(filter.search),
        page,
        rowsPerPage,
        columnOrder,
        organizationId: '',
      }),
    initialData: { data: { data: [] } } as any,
  })

  const handleDelete = async () => {
    try {
      await api.delete(`/organizations/${recordToDelete}`)

      await list.refetch()
    } catch (error) {
      openAlert('Erro ao deletar organização', 'error', error as AxiosError)
    }
  }

  const handleClearFilter = () => {
    setFilter({
      search: '',
      status: 'Todos',
    })
  }

  const handleClose = () => setModalIsOpen(false)

  const onEdit = (id: string) => history.push(`/customers/edit/${id}`)

  const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    event!.preventDefault()
    setPage(newPage)
  }

  const handleChangeRowsPerPage = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleCloseDelete = () => {
    setModalIsOpen(false)
    handleDelete()
  }

  const handleSortChange = (index: number) => {
    columnOrder[index] = columnOrder[index] === 'ASC' ? 'DESC' : 'ASC'

    setColumnOrder(columnOrder)
    list.refetch()
  }

  const onCopyInvite = (inviteLink: string) => {
    if (inviteLink === '' || null) {
      return
    }

    openAlert('Link copiado para a área de transferência!', 'success')
    navigator.clipboard.writeText(inviteLink)
  }

  const onResentInvite = async (id: string) => {
    try {
      await api.post(`/organizations/re-invite`, {
        inviteId: id,
      })
      openAlert('Convite reenviado com sucesso!', 'success')
    } catch (error) {
      openAlert('Erro ao reenviar convite', 'error')
    }
  }

  const onEditInviteEmail = (row: ICustomerDTO) => {
    setInviteToEdit(row)
    setInviteEmail(row.responsibleEmail ?? '')
  }

  const onCloseInviteEmail = () => {
    setInviteToEdit(null)
    setInviteEmail('')
  }

  // `PUT /invites` grava o novo e-mail e ja reenvia o convite com um token novo,
  // numa transacao so. Por isso nao ha um segundo passo chamando `re-invite`:
  // dispararia um segundo e-mail e invalidaria o link recem enviado.
  const onConfirmInviteEmail = async () => {
    if (!inviteToEdit?.inviteId) {
      return
    }

    setIsSavingInviteEmail(true)

    try {
      await api.put('/invites', {
        id: inviteToEdit.inviteId,
        invitedEmail: inviteEmail.trim(),
      })

      onCloseInviteEmail()
      await list.refetch()
      openAlert('E-mail atualizado e convite reenviado!', 'success')
    } catch (error) {
      openAlert('Erro ao atualizar o e-mail do convite', 'error', error as AxiosError)
    } finally {
      setIsSavingInviteEmail(false)
    }
  }

  const onDelete = (id: string) => {
    setModalIsOpen(true)
    setRecordToDelete(id)
  }

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement, globalThis.MouseEvent>, row: ICustomerDTO) => {
    if ((event?.target as HTMLElement).closest('[data-action="true"]')) {
      return
    }

    const orgState = extractState(row)
    updateOrganization(orgState)

    // history.push(`/contract/${row.id}`)
    history.push(`/v2/organizations/${row.id}/workspaces`)
  }

  const listData = list.data?.data.data as ICustomerDTO[]

  return {
    data: listData,
    user,
    isError: list.isError,
    label,
    filter,
    page,
    rowsPerPage,
    modalIsOpen,
    columnOrder,
    inviteToEdit,
    inviteEmail,
    isSavingInviteEmail,
    setInviteEmail,
    onEditInviteEmail,
    onCloseInviteEmail,
    onConfirmInviteEmail,
    setFilter,
    setColumnOrder,
    handleClearFilter,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    handleClose,
    handleCloseDelete,
    handleRowClick,
    onCopyInvite,
    onResentInvite,
    onEdit,
    onDelete,
  }
}

export default useComponent
